import { z } from 'zod';
import { MISSIONS, missionOf } from './missions.js';
/**
 * Cyber passports (task 76, decision 0050): the business shares its checked protections and chosen
 * documents with a named audience (another insurer, a broker, a large customer), with its consent, until a
 * date it picks. What is shared is fixed when it is shared, so the audience sees what was agreed to.
 */
export const PASSPORT_VERSION = 'passport/v1';
export const PASSPORT_TOKEN = /^[A-Za-z0-9_-]{22}$/;
/** How long a pack can stay open, in days. */
export const PASSPORT_DAYS = [7, 30, 90, 180];
export const PASSPORT_MAX_DOCUMENTS = 20;
export const CreatePassport = z.object({
    audience: z
        .string()
        .trim()
        .min(2, 'Say who it is for, e.g. “Kinta Steel procurement”')
        .max(120, 'At most 120 characters'),
    audience_email: z
        .string()
        .trim()
        .toLowerCase()
        .max(254)
        .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter an email address, or leave it empty')
        .nullable()
        .default(null),
    purpose: z.string().trim().max(500, 'At most 500 characters').default(''),
    days: z
        .number()
        .int()
        .refine((d) => PASSPORT_DAYS.includes(d), 'Pick how long it stays open'),
    protections: z.boolean().default(true),
    documents: z
        .array(z.uuid())
        .max(PASSPORT_MAX_DOCUMENTS, `At most ${PASSPORT_MAX_DOCUMENTS} documents`)
        .default([])
        .refine((d) => new Set(d).size === d.length, 'A document is picked twice'),
    consent: z.literal(true, { error: 'Tick the box to agree to share' }),
});
export const PASSPORT_STATE_LABEL = {
    live: 'Shared',
    expired: 'Expired',
    revoked: 'Stopped',
};
/** Evidence beyond the business's own word. */
const CHECKED = new Set(['DOCUMENT_VERIFIED', 'SECURITY_SCAN', 'API_DISCOVERED', 'EXTERNAL_PROVIDER', 'AI_EXTRACTED']); // prettier-ignore
/** The protections a pack shows: every answered control, by mission, in the catalogue's order. */
export function passportProtections(trace) {
    const order = (id) => MISSIONS.findIndex((m) => m.id === id);
    return trace.controls
        .filter((c) => c.status !== 'unanswered')
        .map((c) => ({
        control: c.control,
        name: c.name,
        mission: missionOf(c.control)?.id ?? '',
        status: c.status,
        provenance: c.provenance,
        checked: !!c.checked || (c.provenance !== null && CHECKED.has(c.provenance)),
    }))
        .sort((a, b) => order(a.mission) - order(b.mission) || (a.control < b.control ? -1 : 1));
}
const day = (iso) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur' }); // prettier-ignore
/** The words the owner agrees to, kept with the pack. */
export function consentText(p) {
    const parts = ['CyberScore'];
    if (p.protections)
        parts.push('the protections behind it and how each is backed');
    if (p.documents)
        parts.push(`${p.documents} document${p.documents === 1 ? '' : 's'}`);
    const what = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
    return `I agree to share ${p.business}’s ${what} with ${p.audience} until ${day(p.until)}. Anyone with the link can see it until then, or until I stop sharing.`; // prettier-ignore
}
/** When a pack made now closes: the end of the last day, Malaysia time. */
export function passportExpiry(now, days) {
    const my = new Date(now.getTime() + 8 * 3_600_000);
    const end = Date.UTC(my.getUTCFullYear(), my.getUTCMonth(), my.getUTCDate() + days, 23, 59, 59);
    return new Date(end - 8 * 3_600_000);
}
//# sourceMappingURL=passports.js.map